import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import { Palette } from '@/themes/themeOverrides/Palette.js';
import { Box, Typography, Card, CardContent, ClickAwayListener, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { useState } from 'react';
import { DrawerLink } from '@/components/global/drawers/DrawerLink';
import { ReferenceArrayField, SingleFieldList, FunctionField, ImageField, usePermissions, useTranslate } from 'react-admin';
import NotificationBox from '@/components/global/fields/NotificationBox';
import CustomChipField from '@/components/global/fields/CustomChipField';
import { NoImage } from '@/components/global/fields/NoImage';
import { useNavigate } from 'react-router-dom';
import { CustomButton } from '@/components/global/buttons/CustomButton';
import CustomMorePopover from '@/components/global/buttons/CustomMorePopover';
import CustomEditModalButton from '@/components/global/buttons/CustomEditModalButton';
import CustomShowModalButton from '@/components/global/buttons/CustomShowModalButton';
import FilterIcon from '@/assets/icons/filter-icon.svg?react';
import TransactionProductDetailsWarningIcon from '@/assets/icons/transaction-product-details-warning.svg?react';
import { useDrawer } from '@/composables/useDrawer';
import { buildListFilterUrl } from '@/helpers/filters/buildListFilterUrl.js';
import { useTagNameSource } from '@/i18n/hooks.js';

const StyledTransactionDetailProductCard = styled(Card)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '20px',
    padding: '20px 0 20px 20px',
    flexGrow: 1,
    [theme.breakpoints.up('md')]: {
        padding: '20px 0 20px 32px',
    },
    '& .MuiCardContent-root': {
        flexGrow: 1,
        padding: '0 36px 0 0 !important',
    },
    '& .notification-box': {
        margin: '8px 0 0 0',
        padding: '8px 12px',
        backgroundColor: Palette.yellow.yellow100,
        gap: '8px',
        '& .MuiTypography-root': {
            fontSize: '11px',
            lineHeight: '18px',
            fontWeight: '500',
            letterSpacing: '-0.11px',
        },
        '& .notification-icon': {
            marginRight: '0',
        },
    },
    '& .product-name': {
        fontSize: '14px',
        lineHeight: '20px',
        fontWeight: '600',
        letterSpacing: '-0.14px',
    },
    '& .product-small-text': {
        fontSize: '11px',
        lineHeight: '16px',
        fontWeight: '400',
        letterSpacing: '-0.11px',
        padding: '12px 0',
        '& .MuiTypography-root.MuiTypography-caption': {
            fontSize: '11px',
            lineHeight: '16px',
            fontWeight: '400',
            letterSpacing: '-0.11px',
            display: 'inline-block',
            alignItems: 'center',
            padding: '0 0 0 10px',
        },
        '& .invalid': {
            backgroundColor: Palette.red.red200,
            display: 'inline-block',
            padding: '0 2px',
            borderRadius: '2px',
        },
    },
    '& .product-ean': {
        display: 'inline-block',
        color: Palette.black,
        '&:hover': {
            textDecoration: 'none',
        },
    },
    '& .tag-list': {
        marginBottom: '16px',
        [theme.breakpoints.up('md')]: {
            marginBottom: '24px',
        },
        '& .RaSingleFieldList-link': {
            fontSize: '11px',
            lineHeight: '16px',
            fontWeight: '400',
            letterSpacing: '-0.11px',
            '& p': {
                padding: '0 8px',
                borderRadius: '4px',
                fontSize: '11px',
                lineHeight: '16px',
            },
        },
        '& .MuiStack-root': {
            gap: '4px',
        },
    },
    '& .MuiBox-root .RaImageField-image': {
        width: '100%',
        height: '104px',
        margin: '0',
    },
}));

export const TransactionDetailsProductCard = () => {
    const theme = useTheme();
    const translate = useTranslate();
    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingRight: '36px',
                borderBottom: `2px solid ${Palette.gray.gray200}`,
                position: 'relative',
                [theme.breakpoints.down('md')]: {
                    paddingRight: '0',
                    border: 'none',
                },
                '&::after': {
                    position: 'absolute',
                    bottom: 0,
                    left: '24px',
                    content: '""',
                    display: 'block',
                    width: 'calc(100% - 48px)',
                    height: '2px',
                    backgroundColor: Palette.gray.gray75,
                },
                '&:last-of-type::after': {
                    display: 'none',
                },
            }}
            className='product-card-container'
        >
            <StyledTransactionDetailProductCard className='product-card'>
                <Box
                    className='product-card-image'
                    sx={{
                        width: '120px',
                        height: '120px',
                        [theme.breakpoints.down('md')]: {
                            width: '100px',
                            height: '100px',
                        },
                    }}
                >
                    <FunctionField
                        sx={{ width: '100%', height: '100%' }}
                        render={record =>
                            record.product?.pictureUrl ? (
                                <ImageField
                                    sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    source='product.pictureUrl'
                                />
                            ) : (
                                <NoImage />
                            )
                        }
                    />
                </Box>
                <CardContent>
                    <FunctionField render={record => (record.product ? <ProductCardEanValid /> : <ProductCardEanInvalid />)} />
                </CardContent>
            </StyledTransactionDetailProductCard>
            <FunctionField
                label={translate('common.options')}
                className='product-card-options'
                render={record => (record.product ? <MoreActionsPopover record={record} /> : null)}
            />
        </Box>
    );
};

const ProductCardEanValid = () => {
    const navigate = useNavigate();
    const translate = useTranslate();
    const tagNameSource = useTagNameSource();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const [isMeasurementsTooltipOpen, setIsMeasurementsTooltipOpen] = useState(false);

    return (
        <Box className='product-card-ean-valid'>
            <Box>
                <FunctionField
                    render={record => {
                        return (
                            <Box sx={{ display: 'flex', flexDirection: 'row', gap: '12px', alignItems: 'center' }} className='product-card-name'>
                                <DrawerLink
                                    className='product-ean'
                                    resource='product'
                                    action='show'
                                    source={record.product.id}
                                    label={<span className='product-name'>{record.product.name}</span>}
                                    relative={true}
                                />
                                <span className='product-small-text'>({record.product.ean})</span>
                                {isMobile && (
                                    <ClickAwayListener onClickAway={() => setIsMeasurementsTooltipOpen(false)}>
                                        <Tooltip
                                            open={isMeasurementsTooltipOpen}
                                            onClose={() => setIsMeasurementsTooltipOpen(false)}
                                            disableHoverListener
                                            disableFocusListener
                                            disableTouchListener
                                            title={
                                                <Box className='measurements-tooltip'>
                                                    <Typography variant='caption' component='div'>
                                                        {translate('resources.transaction.labels.measurementsData')}
                                                    </Typography>
                                                    <Typography variant='caption' component='div'>
                                                        {translate('resources.transaction.labels.weight')}:{' '}
                                                        <strong
                                                            className={
                                                                record.validationResult === 'weight_max_invalid' ||
                                                                record.validationResult === 'weight_min_invalid' ||
                                                                record.validationResult === 'weight_invalid'
                                                                    ? 'invalid'
                                                                    : ''
                                                            }
                                                        >
                                                            {record.weight ?? '-'} g
                                                        </strong>{' '}
                                                        / {record.product?.totalWeight ?? 0} g
                                                    </Typography>
                                                    <Typography variant='caption' component='div'>
                                                        {translate('resources.transaction.labels.height')}:{' '}
                                                        <strong className={record.validationResult === 'height_invalid' ? 'invalid' : ''}>
                                                            {record.height ?? '-'} mm
                                                        </strong>{' '}
                                                        / {record.product?.height ?? 0} mm
                                                    </Typography>
                                                    <Typography variant='caption' component='div'>
                                                        {translate('resources.transaction.labels.width')}:{' '}
                                                        <strong className={record.validationResult === 'width_invalid' ? 'invalid' : ''}>
                                                            {record.width ?? '-'} mm
                                                        </strong>{' '}
                                                        / {record.product?.width ?? 0} mm
                                                    </Typography>
                                                </Box>
                                            }
                                            placement='bottom-end'
                                            slotProps={{
                                                popper: {
                                                    modifiers: [
                                                        {
                                                            name: 'offset',
                                                            options: { offset: [0, -8] },
                                                        },
                                                    ],
                                                },
                                            }}
                                            classes={{ tooltip: 'tooltip-white tooltip-white--small measurements-tooltip' }}
                                        >
                                            <Box
                                                component='button'
                                                type='button'
                                                className='product-small-text'
                                                onClick={e => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setIsMeasurementsTooltipOpen(open => !open);
                                                }}
                                                sx={{
                                                    all: 'unset',
                                                    cursor: 'pointer',
                                                    textDecoration: 'underline',
                                                    font: 'inherit',
                                                    fontSize: '11px',
                                                    lineHeight: '16px',
                                                    letterSpacing: '-0.11px',
                                                }}
                                            >
                                                {translate('resources.transaction.labels.measurements')}
                                            </Box>
                                        </Tooltip>
                                    </ClickAwayListener>
                                )}
                            </Box>
                        );
                    }}
                />
                <ReferenceArrayField
                    source='tagIds'
                    reference='tag'
                    label={translate('resources.product.fields.tags')}
                    sort={{ field: 'sortOrder', order: 'ASC' }}
                    className='tag-list'
                >
                    <SingleFieldList linkType={false}>
                        <CustomChipField
                            source={tagNameSource}
                            onClick={record => {
                                navigate(
                                    buildListFilterUrl('/transaction', { 'transactionContainers->product->tags->id_in': [record.id] }, [
                                        'transactionContainers->product->tags->id_in',
                                    ]),
                                );
                            }}
                            shades={true}
                        />
                    </SingleFieldList>
                </ReferenceArrayField>
                <FunctionField
                    render={record => {
                        let validationNotification = null;

                        if (record.status === 'ean_invalid') {
                            validationNotification = (
                                <NotificationBox variant='warning' icon={<TransactionProductDetailsWarningIcon />}>
                                    {translate('resources.transaction.labels.eanInvalidOnDevice')}
                                </NotificationBox>
                            );
                        } else if (record.validationResult !== 'validation_ok') {
                            if (record.validationResult === 'height_invalid') {
                                validationNotification = (
                                    <NotificationBox variant='warning' icon={<TransactionProductDetailsWarningIcon />}>
                                        {translate(`resources.transaction.productsValidation.${record.validationResult}`)}{' '}
                                        <strong>{record.height ?? '-'} mm</strong>
                                    </NotificationBox>
                                );
                            } else if (record.validationResult === 'width_invalid') {
                                validationNotification = (
                                    <NotificationBox variant='warning' icon={<TransactionProductDetailsWarningIcon />}>
                                        {translate(`resources.transaction.productsValidation.${record.validationResult}`)}{' '}
                                        <strong>{record.width ?? '-'} mm</strong>
                                    </NotificationBox>
                                );
                            } else if (
                                record.validationResult === 'weight_invalid' ||
                                record.validationResult === 'weight_max_invalid' ||
                                record.validationResult === 'weight_min_invalid'
                            ) {
                                validationNotification = (
                                    <NotificationBox variant='warning' icon={<TransactionProductDetailsWarningIcon />}>
                                        {translate(`resources.transaction.productsValidation.${record.validationResult}`)}{' '}
                                        <strong>{record.weight ?? '-'} g</strong>
                                    </NotificationBox>
                                );
                            } else {
                                validationNotification = (
                                    <NotificationBox variant='warning' icon={<TransactionProductDetailsWarningIcon />}>
                                        {translate(`resources.transaction.productsValidation.${record.validationResult}`)}
                                    </NotificationBox>
                                );
                            }
                        }

                        return (
                            <>
                                <Box>{validationNotification}</Box>
                                {!isMobile && (
                                    <Box className='product-measurements product-small-text'>
                                        {translate('resources.transaction.labels.measurementsData')}
                                        <Typography variant='caption'>
                                            {translate('resources.transaction.labels.weight')}:{' '}
                                            <strong
                                                className={
                                                    record.validationResult === 'weight_max_invalid' ||
                                                    record.validationResult === 'weight_min_invalid' ||
                                                    record.validationResult === 'weight_invalid'
                                                        ? 'invalid'
                                                        : ''
                                                }
                                            >
                                                {record.weight ?? '-'} g
                                            </strong>{' '}
                                            / {record.product?.totalWeight ?? 0} g
                                        </Typography>
                                        <Typography variant='caption'>
                                            {translate('resources.transaction.labels.height')}:{' '}
                                            <strong className={record.validationResult === 'height_invalid' ? 'invalid' : ''}>{record.height ?? '-'} mm</strong>{' '}
                                            / {record.product?.height ?? 0} mm
                                        </Typography>
                                        <Typography variant='caption'>
                                            {translate('resources.transaction.labels.width')}:{' '}
                                            <strong className={record.validationResult === 'width_invalid' ? 'invalid' : ''}>{record.width ?? '-'} mm</strong> /{' '}
                                            {record.product?.width ?? 0} mm
                                        </Typography>
                                    </Box>
                                )}
                            </>
                        );
                    }}
                />
            </Box>
        </Box>
    );
};

const ProductCardEanInvalid = () => {
    const translate = useTranslate();
    return (
        <FunctionField
            render={record => {
                return (
                    <>
                        <Typography variant='h6' sx={{ fontWeight: '600', letterSpacing: '-0.14px' }}>
                            {translate('resources.transaction.labels.ean')}: {record.ean}
                        </Typography>
                        <NotificationBox variant='warning' icon={<TransactionProductDetailsWarningIcon />}>
                            {translate('resources.transaction.labels.eanInvalid')}
                        </NotificationBox>
                        <Box className='product-measurements product-small-text'>
                            {translate('resources.transaction.labels.measurementsData')}
                            <Typography variant='caption'>
                                {translate('resources.transaction.labels.weight')}: <strong>{record.weight ?? '-'} g</strong> /{' '}
                                {record.product?.totalWeight ?? '-'} g
                            </Typography>
                            <Typography variant='caption'>
                                {translate('resources.transaction.labels.height')}: <strong>{record.height ?? '-'} mm</strong> / {record.product?.height ?? '-'}{' '}
                                mm
                            </Typography>
                            <Typography variant='caption'>
                                {translate('resources.transaction.labels.width')}: <strong>{record.width ?? '-'} mm</strong> / {record.product?.width ?? '-'} mm
                            </Typography>
                        </Box>
                    </>
                );
            }}
        />
    );
};

const MoreActionsPopover = ({ record }) => {
    const { isLoading, permissions } = usePermissions();
    const { openDrawer } = useDrawer();
    const navigate = useNavigate();
    const translate = useTranslate();

    const handleClick = e => {
        e.stopPropagation();
        navigate(
            buildListFilterUrl('/transaction', { 'transactionContainers->product->id_in': [record.product.id] }, ['transactionContainers->product->id_in']),
            { replace: true },
        );
    };

    if (isLoading || !record) return null;

    return (
        <CustomMorePopover record={record}>
            <CustomShowModalButton onOpen={() => openDrawer(record.product.id, 'product-show')} label={translate('resources.product.labels.showProduct')} />
            {permissions.isAdmin && (
                <CustomEditModalButton onOpen={() => openDrawer(record.product.id, 'product-edit')} label={translate('resources.product.labels.editProduct')} />
            )}
            <CustomButton variant='text' label={translate('resources.transaction.labels.filterByProduct')} onClick={handleClick}>
                <FilterIcon />
            </CustomButton>
        </CustomMorePopover>
    );
};

ProductCardEanInvalid.propTypes = {
    record: PropTypes.shape({
        ean: PropTypes.string,
        weight: PropTypes.number || PropTypes.string,
        height: PropTypes.number || PropTypes.string,
        width: PropTypes.number || PropTypes.string,
    }),
};

MoreActionsPopover.propTypes = {
    record: PropTypes.shape({
        product: PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string,
            ean: PropTypes.string,
        }).isRequired,
        status: PropTypes.string,
        validationResult: PropTypes.string,
        pictureUrl: PropTypes.string,
        tagIds: PropTypes.array,
    }).isRequired,
};
